import React, { useState, useEffect, useRef} from 'react';
import {
	Close as CloseIcon,
	KeyboardArrowLeft as ArrowLeftIcon,
	KeyboardArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	IconButton,
	LinearProgress,
	Popper,
	Typography,
} from '@mui/material';
import useTheme from '../../hooks/useTheme';
import { SuccessActivityIcon } from '../../assets/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
	DELETE_ACTIVITY_BANNER_ACTION,
	DELETE_ALL_ACTIVITY_BANNER_ACTION,
} from '../../constants/activityBannerConstants';
import Draggable from 'react-draggable';
import { Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
/**
 * Renders activity banner common component
 *
 * @function
 * @return {JSX.Element}
 */
export function ActivityBanner() {
	const { FontFace, color } = useTheme();

	const dispatch = useDispatch();
	const messages = useSelector((state) => {
		return state.activityBannerReducer?.activityMessages;
	});
	const loginState = useSelector((state) => state?.userLogin);
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
	const totalMessageCount = messages?.length ?? 0;
	const [isDragging, setIsDragging] = useState(false);
	const [hoveringMessage, setHoveringMessage] = useState(false);

	// Load last position from localStorage or set default
	const [position, setPosition] = useState(() => {
		const savedPosition = localStorage.getItem('activityBannerPosition');
		return savedPosition
			? JSON.parse(savedPosition)
			: { x: 32, y: window.innerHeight - 100 -24};
	});

	useEffect(() => {
		const savedPosition = localStorage.getItem('activityBannerPosition');
		// If a saved position exists, do not override it.
		if (savedPosition) return;
		/**
		 * @function
		 */
		function updatePosition() {
			setPosition({
				x: 32,
				y: window.innerHeight - 100 -24
			});
		}

		updatePosition();
		window.addEventListener('resize', updatePosition);
		return () => window.removeEventListener('resize', updatePosition);
	}, []);

	useEffect(() => {
		if (messages && messages.length > 0) {
			setCurrentMessageIndex(messages.length - 1);
		} else {
			setCurrentMessageIndex(0);
		}
	}, [messages]);

	useEffect(() => {
		if (!loginState?.userInfo?.authorizationToken) {
			handleClearAll();
		}
	}, [loginState]);
	// Save position when drag stops
	const handleStop = (e, data) => {
		const newPosition = { x: data.x, y: data.y };
		setPosition(newPosition);
		localStorage.setItem(
			'activityBannerPosition',
			JSON.stringify(newPosition)
		);
		setIsDragging(false);
	};

	const handleClearAll = () => {
		setCurrentMessageIndex(0);
		dispatch({
			type: DELETE_ALL_ACTIVITY_BANNER_ACTION,
		});
	};
	const handleRemoveCurrent = () => {
		dispatch({
			type: DELETE_ACTIVITY_BANNER_ACTION,
			payload: messages[currentMessageIndex],
		});
		setCurrentMessageIndex((prev) =>
			Math.max(0, Math.min(prev, totalMessageCount - 2))
		);
	};

	const handlePrevMessage = () => {
		setCurrentMessageIndex((prev) => Math.max(prev - 1, 0));
	};

	const handleNextMessage = () => {
		setCurrentMessageIndex((prev) =>
			Math.min(prev + 1, totalMessageCount - 1)
		);
	};

	const messageTimePastSinceNow = (messageTime) => {
		return Math.round(
			(Date.now() - (messageTime ?? Date.now())) / (1000 * 60)
		);
	};

	const currentMessageTitle = messages[currentMessageIndex]?.title;
	const currentMessageBody = messages[currentMessageIndex]?.message;
	const currentMessageTime = messages[currentMessageIndex]?.messageTime;
	const relativeTimeInMins = messageTimePastSinceNow(currentMessageTime);
	const currentMessageVariant = messages[currentMessageIndex]?.variant;
	const currentMessageProgress = messages[currentMessageIndex]?.progress;

	// const sidebar = document.querySelector('#sidebar');
	// const recr = sidebar?.getBoundingClientRect();

	// const virtualAnchor = {
	// 	getBoundingClientRect: () => ({
	// 		top: window.innerHeight - 20,
	// 		left: recr.right + recr.width * 3,
	// 		// bottom: 20,
	// 		width: 0,
	// 		height: 0,
	// 	}),
	// 	contextElement: document.body,
	// };
	// Custom tooltip implementation (modeled after your DataTableHead)
	const CustomTooltip = ({ title, open, children }) => {
		const [visible, setVisible] = useState(false);
		const delayTimeout = useRef(null);

		useEffect(() => {
			if (open) {
				delayTimeout.current = setTimeout(() => {
					setVisible(true);
				}, 100);
			} else {
				clearTimeout(delayTimeout.current);
				setVisible(false);
			}
			return () => {
				clearTimeout(delayTimeout.current);
			};
		}, [open]);

		return (
			<Tooltip
				title={title}
				open={visible}
				TransitionProps={{
					appear: true,
					timeout: { enter: 500, exit: 300 },
				}}
				componentsProps={{
					tooltip: {
						sx: {
							padding: '4px 8px',
							boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.16)',
							border: 'solid 1px #dedede',
							backgroundColor: '#fff',
							color: '#000',
							fontFamily: 'Inter-Regular',
							fontSize: '12px',
							fontWeight: 'medium',
							fontWeight: 'normal',
						},
					},
				}}
			>
				{children}
			</Tooltip>
		);
	};

	const messageRef = useRef(null);
	const [isClipped, setIsClipped] = useState(false);

	useEffect(() => {
	if (messageRef.current) {
		// Check if the element is clipped (scrollHeight > clientHeight)
		setIsClipped(messageRef.current.scrollHeight > messageRef.current.clientHeight);
	}
	}, [currentMessageBody]);
	return (
		<Popper
			id="activity-banner"
			open={totalMessageCount > 0}
			style={{ zIndex: 1300, pointerEvents: 'none' }}
			placement="top-end"
		>
			{messages && (
				<Draggable
					handle=".draggable-handle"
					position={position}
					onStart={() => setIsDragging(true)}
					onStop={handleStop}
				>
					<div style={{ pointerEvents: 'auto' }}>
						<Card
							sx={{
								width: '500px',
								height: '100px',
								borderRadius: '4px',
								boxShadow: totalMessageCount > 1 ? '0px 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
								border: '2px solid rgba(0, 0, 0, 0.12)',
								position: 'relative',
							}}
						>
							<Box
								display="flex"
								flexDirection="row"
								bgcolor="white"
								p={2}
								className="draggable-handle"
								sx={{
									cursor: isDragging ? 'grabbing' : 'default',
									paddingBottom: '8px',
								}}
							>
								{currentMessageVariant !== 'progress' && (
									<Box flex={0.5} display="flex" justifyContent="center">
										<SuccessActivityIcon />
									</Box>
								)}
								<Box width={'16px'} height={'18px'} />
								<Box flex={6.5}>
									<Box display="flex" justifyContent="space-between" alignItems="flex-start">
										<Typography
											fontFamily="Inter-Medium"
											fontSize={FontFace.font_size.x_small}
											fontWeight={FontFace.font_weight.bold}
											color={color.text.primary}
											lineHeight="1.2"
										>
											{currentMessageTitle}
										</Typography>
									</Box>
									<Box height={'8px'} />
									{currentMessageBody && (
										<CustomTooltip
											title={<span dangerouslySetInnerHTML={{ __html: currentMessageBody }} />}
											open={hoveringMessage}
										>
											<Box
												ref={messageRef}
												onMouseEnter={() => { if (isClipped) setHoveringMessage(true); }}
												onMouseLeave={() => setHoveringMessage(false)}
												sx={{
													display: '-webkit-box',
													WebkitBoxOrient: 'vertical',
													WebkitLineClamp: 2,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													wordBreak: 'break-word',
													fontSize: FontFace.font_size.xx_inputlabel,
													marginBottom: '-4px',
													lineHeight: '1.4',
												}}
												dangerouslySetInnerHTML={{ __html: currentMessageBody }}
											/>
										</CustomTooltip>
									)}
									{currentMessageVariant === 'progress' && <Box height={'16px'} />}
									{currentMessageVariant === 'progress' && currentMessageProgress != null && (
										<LinearProgress
											variant="determinate"
											value={currentMessageProgress}
											sx={{ color: color.background.brand }}
											color={'inherit'}
										/>
									)}
								</Box>
								<Box
									display="flex"
									flexDirection="column"
									flex={2}
									justifyContent="space-between"
									height="100%"
									marginTop={'-6px'}
								>
									<Box display="flex" justifyContent="flex-end" alignItems="center">
										{totalMessageCount > 1 && (
											<Button
												variant="text"
												onClick={handleClearAll}
												sx={{ px: 0, minWidth: 'auto', mr: 1 }}
											>
												<Typography
													sx={{
														textDecoration: 'underline',
														fontSize: FontFace.font_size.xx_small,
														color: color.text.secondary,
													}}
												>
													Clear All
												</Typography>
											</Button>
										)}
										<IconButton size="small" onClick={handleRemoveCurrent}>
											<CloseIcon fontSize="small" />
										</IconButton>
									</Box>
								</Box>
							</Box>
							<Box
								sx={{
									position: 'absolute',
									bottom: 0,
									left: currentMessageVariant === 'progress' ? 32 : 58,
									right: 14,
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								{relativeTimeInMins != null && !isNaN(relativeTimeInMins) && (
									<Typography
										fontFamily="Inter-Regular"
										fontSize={FontFace.font_size.xx_inputlabel}
										color={color.text.secondary}
									>
										{relativeTimeInMins === 0 ? 'Now' : `${relativeTimeInMins}m`}
									</Typography>
								)}
								{totalMessageCount > 1 && (
									<Box display="flex" alignItems="center">
										<IconButton
											disabled={currentMessageIndex === 0}
											onClick={handlePrevMessage}
											size="small"
										>
											<ArrowLeftIcon fontSize="small" />
										</IconButton>
										<Typography
											fontFamily="Inter-Regular"
											fontSize={FontFace.font_size.xx_inputlabel}
											color={color.text.secondary}
											mx={1}
										>
											{currentMessageIndex + 1}/{totalMessageCount}
										</Typography>
										<IconButton
											disabled={currentMessageIndex + 1 >= totalMessageCount}
											onClick={handleNextMessage}
											size="small"
										>
											<ArrowRightIcon fontSize="small" />
										</IconButton>
									</Box>
								)}
							</Box>
						</Card>
					</div>
				</Draggable>
			)}
		</Popper>
	);
}

ActivityBanner.defaultProps = {};
ActivityBanner.propTypes = {
	title: PropTypes.string,
		open: PropTypes.bool,
		children: PropTypes.element,
}